/**
 * 「待归档」详情页（v1 · 浏览 + 预览）
 *
 * 路由：/ai-classify/unclassified（常量 UNCLASSIFIED_DETAIL_ROUTE）
 *
 * 列表页「待归档」虚拟文件夹的点击落点。展示团队全部未被任何顶级 AI 智能
 * 文件夹规则命中的照片（接口 13，游标分页）。
 *
 * 与常规文件夹详情页（Detail/index.tsx）的区别：
 *  · 无 folderID / 无规则气泡 / 无子文件夹
 *  · 游标分页（useUnclassifiedPhotos）而非页码
 *  · 照片结构对齐 FolderPhoto，复用 PhotoGrid + adaptFolderPhoto + ViewerV2
 *
 * v1 仅浏览 + 大图预览，不接多选 / 批量操作（后续迭代再补）。
 */

import React, { useCallback, useEffect, useRef } from 'react'
import { Button, Spin } from 'antd'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useAppSelector } from '../../../app/hooks'
import Viewer from '../../components/ViewerV2'
import type { ViewerRef } from '../../components/ViewerV2/types'
import PhotoGrid from '../Detail/PhotoGrid'
import { UNCLASSIFIED_FOLDER_NAME } from '../components/FolderList/unclassifiedFolder'
import { trackDetailClick } from '../utils/trackClassify'
import { useUnclassifiedPhotos } from './useUnclassifiedPhotos'
import styles from './Unclassified.module.scss'

const UnclassifiedDetail: React.FC = () => {
  const navigate = useNavigate()
  const groupID = useAppSelector((s) => s.group.groupId)

  const { photos, loading, hasMore, loadMore } = useUnclassifiedPhotos(groupID)

  /** 大图预览 ViewerV2 ref · 单图点击调 viewerRef.current.view(index) 打开 */
  const viewerRef = useRef<ViewerRef>(null)

  useEffect(() => {
    trackDetailClick('enterPage', { path: UNCLASSIFIED_FOLDER_NAME })
  }, [])

  const handleBack = useCallback(() => {
    navigate('/ai-classify')
  }, [navigate])

  /** 照片点击预览 · 找到 id 在 photos 中的 index 后调 viewer */
  const handlePreview = useCallback(
    (id: string) => {
      const idx = photos.findIndex((p) => p.id === id)
      if (idx >= 0 && viewerRef.current) {
        viewerRef.current.view(idx)
      }
    },
    [photos]
  )

  const isInitialLoading = loading && photos.length === 0
  // loadMore 跨页累加到 100 张或翻到底，所以「不在加载且一张没有」即真·空（已到底）
  const isEmpty = !loading && photos.length === 0

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Button
          type="link"
          className={styles.backBtn}
          onClick={handleBack}
          aria-label="返回文件夹列表"
        >
          <ArrowLeftOutlined /> 返回
        </Button>
        <span className={styles.title}>{UNCLASSIFIED_FOLDER_NAME}</span>
      </div>

      <div className={styles.content}>
        {isInitialLoading ? (
          <div className={styles.empty}>
            <Spin tip="加载中..." />
          </div>
        ) : isEmpty ? (
          <div className={styles.empty}>暂无待归档照片</div>
        ) : (
          <PhotoGrid
            photos={photos}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={loadMore}
            onPreview={handlePreview}
          />
        )}
      </div>

      {/* 大图预览 · 与常规详情页同款 ViewerV2 · 关掉删除 / 整改 / 收藏
          不传 isHideFoldersInfo → 展示"所属文件夹"，含管理员"添加到文件夹"写操作 */}
      <Viewer
        ref={viewerRef}
        photos={photos as never}
        onFetchMore={loadMore}
        canDelete={false}
        canRectify={false}
        canFavorite={false}
        fromPlace="aiClassifyUnclassified"
      />
    </div>
  )
}

export default UnclassifiedDetail
